import {Injectable, inject} from '@angular/core';
import {Action} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {MemoizedSelector, Store} from '@ngrx/store';
import {Observable, catchError, filter, map, of, switchMap, tap, withLatestFrom} from 'rxjs';
import {NotificationService} from '@totalenergiescode/tds-angular';
import {TranslateService} from '@ngx-translate/core';
import {PortfolioSimulatorService} from '../services/portfolio-simulator-service';
import {PortfolioSimulatorActions} from './portfolio-simulator.actions';
import {
  selectPriceDataset,
  selectVersion,
  selectWeatherYear,
} from './portfolio-simulator.reducer';
import {
  selectShouldLoadAssets,
  selectShouldLoadOfftakes,
  selectShouldLoadPriceDatasets,
  selectShouldLoadVersions,
} from './portfolio-simulator.selectors';
import {PORTFOLIO_LIVE_VERSION_NAME} from '../../../shared/model/constants';

@Injectable()
export class PortfolioSimulatorEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly portfolioService = inject(PortfolioSimulatorService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);

  loadAssets$ = this.createLoadEffect(
    selectShouldLoadAssets,
    (version) => this.portfolioService.getAssets(version),
    (assets, version) =>
      PortfolioSimulatorActions.loadAssetsSuccess({assets, version}),
    (error) => PortfolioSimulatorActions.loadAssetsFailure({error}),
  );

  loadOfftakes$ = this.createLoadEffect(
    selectShouldLoadOfftakes,
    (version) => this.portfolioService.getOfftakes(version),
    (offtakes, version) =>
      PortfolioSimulatorActions.loadOfftakesSuccess({offtakes, version}),
    (error) => PortfolioSimulatorActions.loadOfftakesFailure({error}),
  );

  loadVersions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PortfolioSimulatorActions.init,
        PortfolioSimulatorActions.updateInputs,
      ),
      withLatestFrom(
        this.store.select(selectShouldLoadVersions),
        this.store.select(selectPriceDataset),
      ),
      filter(([, shouldLoad]) => shouldLoad),
      // switchMap cancels any in-flight request when a newer action passes the guard
      switchMap(([, , priceDataset]) =>
        this.portfolioService.getVersions(priceDataset || undefined).pipe(
          map((versions) =>
            PortfolioSimulatorActions.loadVersionsSuccess({versions}),
          ),
          catchError((error: unknown) =>
            of(
              PortfolioSimulatorActions.loadVersionsFailure({
                error: String(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  autoSelectVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PortfolioSimulatorActions.loadVersionsSuccess),
      map(({versions}) => {
        const target =
          versions.find((v) => v.versionName === PORTFOLIO_LIVE_VERSION_NAME) ??
          versions[0];
        return target
          ? PortfolioSimulatorActions.updateInputs({version: target.versionName})
          : null;
      }),
      filter(
        (action): action is ReturnType<typeof PortfolioSimulatorActions.updateInputs> =>
          action !== null,
      ),
    ),
  );

  loadPriceDatasets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        PortfolioSimulatorActions.init,
        PortfolioSimulatorActions.updateInputs,
      ),
      withLatestFrom(
        this.store.select(selectShouldLoadPriceDatasets),
        this.store.select(selectVersion),
      ),
      filter(([, shouldLoad]) => shouldLoad),
      // switchMap cancels any in-flight request when a newer action passes the guard
      switchMap(([, , version]) =>
        this.portfolioService.getPriceDatasets(version || undefined).pipe(
          map((priceDatasets) =>
            PortfolioSimulatorActions.loadPriceDatasetsSuccess({priceDatasets}),
          ),
          catchError((error: unknown) =>
            of(
              PortfolioSimulatorActions.loadPriceDatasetsFailure({
                error: String(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  autoSelectPriceDataset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PortfolioSimulatorActions.loadPriceDatasetsSuccess),
      withLatestFrom(this.store.select(selectPriceDataset)),
      map(([{priceDatasets}, currentPriceDataset]) => {
        const stillValid = priceDatasets.some(
          (pd) => pd.priceDataset === currentPriceDataset,
        );
        if (stillValid) return null;
        const target = priceDatasets[0];
        return target
          ? PortfolioSimulatorActions.updateInputs({
              priceDataset: target.priceDataset,
            })
          : null;
      }),
      filter(
        (action): action is ReturnType<typeof PortfolioSimulatorActions.updateInputs> =>
          action !== null,
      ),
    ),
  );

  notifyAssetsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PortfolioSimulatorActions.loadAssetsFailure),
        tap(() =>
          this.showErrorNotification('portfolioSimulator.loadAssetsError'),
        ),
      ),
    {dispatch: false},
  );

  notifyOfftakesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PortfolioSimulatorActions.loadOfftakesFailure),
        tap(() =>
          this.showErrorNotification('portfolioSimulator.loadOfftakesError'),
        ),
      ),
    {dispatch: false},
  );

  notifyVersionsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PortfolioSimulatorActions.loadVersionsFailure),
        tap(() =>
          this.showErrorNotification('portfolioSimulator.loadVersionsError'),
        ),
      ),
    {dispatch: false},
  );

  notifyPriceDatasetsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PortfolioSimulatorActions.loadPriceDatasetsFailure),
        tap(() =>
          this.showErrorNotification(
            'portfolioSimulator.loadPriceDatasetsError',
          ),
        ),
      ),
    {dispatch: false},
  );

  loadSimulationResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PortfolioSimulatorActions.displayResults),
      withLatestFrom(
        this.store.select(selectVersion),
        this.store.select(selectWeatherYear),
      ),
      filter(([, version]) => version !== ''),
      switchMap(([, version, weatherYear]) =>
        this.portfolioService
          .getSimulationResults(version, weatherYear)
          .pipe(
            map((results) =>
              PortfolioSimulatorActions.loadSimulationResultsSuccess({results}),
            ),
            catchError((error: unknown) =>
              of(
                PortfolioSimulatorActions.loadSimulationResultsFailure({
                  error: String(error),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  notifySimulationResultsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PortfolioSimulatorActions.loadSimulationResultsFailure),
        tap(() =>
          this.showErrorNotification(
            'portfolioSimulator.loadSimulationResultsError',
          ),
        ),
      ),
    {dispatch: false},
  );

  private showErrorNotification(messageKey: string): void {
    this.notificationService.openNotification({
      status: 'danger',
      content: this.translateService.instant(messageKey),
      closeIconButtonLabel: this.translateService.instant(
        'portfolioSimulator.closeNotification',
      ),
    });
  }

  private createLoadEffect<T>(
    shouldLoadSelector: MemoizedSelector<object, boolean>,
    serviceCall: (version: string) => Observable<T[]>,
    successAction: (data: T[], version: string) => Action,
    failureAction: (error: string) => Action,
  ) {
    return createEffect(() =>
      this.actions$.pipe(
        ofType(
          PortfolioSimulatorActions.init,
          PortfolioSimulatorActions.updateInputs,
        ),
        withLatestFrom(
          this.store.select(selectVersion),
          this.store.select(shouldLoadSelector),
        ),
        filter(([, version, shouldLoad]) => shouldLoad && version !== ''),
        switchMap(([, version]) =>
          serviceCall(version).pipe(
            map((data) => successAction(data, version)),
            catchError((error: unknown) => of(failureAction(String(error)))),
          ),
        ),
      ),
    );
  }
}
